/**
 * Maps a slideshow's sound-timeline segments to absolute playback windows so audio can be
 * synced to the slide timeline's playhead.
 *
 * Songs play sequentially from t=0 in segment order (matching the sound row's left-to-right
 * strips): the first song occupies `[0, d1)`, the next `[d1, d1 + d2)`, and so on, where each
 * song's on-timeline length is its trimmed duration `endMs - startMs`. Trimming is not editable
 * yet (FOR-16165), so today `startMs` is 0 and `endMs` is the full song duration.
 */

export interface ISongPlaybackWindow {
    songId: string;
    /** Inclusive start of this song on the slideshow timeline (ms). */
    timelineStartMs: number;
    /** Exclusive end of this song on the slideshow timeline (ms). */
    timelineEndMs: number;
    /** Offset into the song where playback begins (ms) — the trim start. */
    trimStartMs: number;
    /** Pre-resolved stream URL embedded on the segment, if any — see ISlideshowSongSegment. */
    streamUrl?: string | null;
}

type SongSegmentInput = Pick<
    SpApi.ISlideshowSongSegment,
    'songId' | 'startMs' | 'endMs' | 'streamUrl'
>;

/**
 * Builds sequential playback windows from sound-timeline segments (already in display order).
 * Segments without a positive trimmed duration or a song id are skipped so they never claim
 * timeline space or leave a silent gap.
 */
export function buildSongPlaybackWindows(segments: SongSegmentInput[]): ISongPlaybackWindow[] {
    const windows: ISongPlaybackWindow[] = [];
    let cursorMs = 0;

    segments.forEach((segment) => {
        const trimStartMs = Math.max(0, segment.startMs ?? 0);
        const trimEndMs = Math.max(trimStartMs, segment.endMs ?? 0);
        const durationMs = trimEndMs - trimStartMs;

        if (!segment.songId || durationMs <= 0) {
            return;
        }

        windows.push({
            songId: String(segment.songId),
            timelineStartMs: cursorMs,
            timelineEndMs: cursorMs + durationMs,
            trimStartMs,
            streamUrl: segment.streamUrl ?? null
        });

        cursorMs += durationMs;
    });

    return windows;
}

/**
 * Returns the playback window active at the given timeline position, or null when the playhead
 * sits before the first song, in a gap, or past the last song.
 */
export function findActiveSongWindow(
    windows: ISongPlaybackWindow[],
    timelineMs: number
): ISongPlaybackWindow | null {
    return (
        windows.find(
            (window) => timelineMs >= window.timelineStartMs && timelineMs < window.timelineEndMs
        ) ?? null
    );
}

/**
 * The position (seconds) the song's audio element should be at for a given timeline position,
 * i.e. the trim start plus how far the playhead has advanced into this song's window.
 */
export function getSongPlaybackOffsetSeconds(
    window: ISongPlaybackWindow,
    timelineMs: number
): number {
    return (window.trimStartMs + (timelineMs - window.timelineStartMs)) / 1000;
}
